import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
    Grid,
    Pagination,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Button,
    Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { SCUIBreadCrumb } from '../../../atomic/molecules';
import { SCUIButton } from '../../../atomic/atoms';
import { Menu, MenuItem, IconButton } from '@mui/material';
import { bindTrigger, bindMenu } from 'material-ui-popup-state/hooks';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PopupState from 'material-ui-popup-state';
import { useJsonTemplate } from '../context/JsonTemplateContext';
import Swal from 'sweetalert2';
import { JsonbuilderServiceCommand } from '../../../services/command';
import { DJBL, djblCls } from '../dynamicJsonBuilder.classes';

export const JsonTemplateLayout: FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const {
        filteredDatas,
        allRowCount,
        currentPage,
        searchKeyword,
        setCurrentPage,
        getTemplates,
        handleSearch,
        handleKeyPress,
        handleFilter,
    } = useJsonTemplate();

    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage);
    };

    useEffect(() => {
        getTemplates();
    }, []);

    useEffect(() => {
        if (currentPage > 1) {
            getTemplates();
        }
    }, [currentPage]);

    const handleDeleteClick = async (id: number) => {
        try {
            const result = await Swal.fire({
                title: t('general.deleteConfirmationText'),
                text: t('general.noReturn'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('general.yes'),
                cancelButtonText: t('general.no'),
            });

            if (result.isConfirmed) {
                await JsonbuilderServiceCommand.DeleteTemplate(id);
                await getTemplates();
                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: t('general.deleteOperationSuccessMessage'),
                });
            }
        } catch (error) {
            console.error('Error deleting template:', error);
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('general.somethingWentWrong'),
            });
        }
    };

    const handleClick = async (popupState: any, tur: string, id: number) => {
        popupState.close();
        switch (tur) {
            case 'edit': {
                try {
                    history.push({ pathname: '/json-template/edit-json-template/' + id });
                } catch (error) {
                    console.error('Error fetching template details:', error);
                }
                break;
            }
            case 'delete': {
                handleDeleteClick(id);
                break;
            }
            default: {
                break;
            }
        }
    };

    const handleCreateNewTemplate = () => {
        try {
            history.push('/json-template/add-json-template');
        } catch (error) {
            console.error('Error during navigation:', error);
        }
    };

    return (
        <>
            <SCUIBreadCrumb
                dontShowHome={true}
                homeIconLink={'/home'}
                onlyTitle={t('jsonTemplateBuilder.jsonTemplateBuilder')}
                components={[
                    {
                        title: t('jsonTemplateBuilder.jsonTemplateBuilder'),
                        link: '/json-template',
                    },
                ]}
            />
            <div className={djblCls('lineWrapper', DJBL.divider)} />
            <Grid className={DJBL.toolbar} container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid
                    className={DJBL.actionsPrimary}
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                    size={{
                        xs: 12,
                    }}
                >
                    <SCUIButton
                        className={DJBL.inputButtonCreate}
                        onClick={handleCreateNewTemplate}
                        startIcon={<AddIcon sx={{ color: 'white' }} />}
                        showStartIcon
                        style={{ whiteSpace: 'nowrap', margin: '16px 0' }}
                        type={'primary'}
                        sizeType={'fixed'}
                        size={'s'}
                        text={t('jsonTemplateBuilder.createNewJsonTemplate')}
                    />
                </Grid>

                <Grid className={DJBL.filtersRow} display="flex" gap={3} alignItems="center">
                    <TextField
                        className={DJBL.inputSearchKeyword}
                        placeholder={t('general.search')}
                        onChange={handleSearch}
                        onKeyDown={handleKeyPress}
                        value={searchKeyword}
                        size="small"
                        sx={{
                            width: '300px',
                            '& .MuiInputBase-root': {
                                height: '40px',
                            },
                        }}
                    />
                    <SCUIButton
                        className={DJBL.inputButtonFilter}
                        text={t('general.filter')}
                        type="primary"
                        sizeType="fixed"
                        size={'s'}
                        onClick={handleFilter}
                    />
                </Grid>
            </Grid>
            <TableContainer className={DJBL.tableContainer}>
                <Table className={DJBL.table} component={Paper} style={{ marginBottom: '2rem' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ textAlign: 'center', minWidth: 250 }}>
                                {t('jsonTemplateBuilder.templateName')}
                            </TableCell>
                            <TableCell style={{ textAlign: 'center', minWidth: 200 }}>
                                {t('jsonTemplateBuilder.teamName')}
                            </TableCell>
                            <TableCell style={{ textAlign: 'center', minWidth: 100 }}>
                                {t('jsonTemplateBuilder.createBy')}
                            </TableCell>
                            <TableCell style={{ textAlign: 'center', minWidth: 100 }}>
                                {t('jsonTemplateBuilder.createDate')}
                            </TableCell>
                            <TableCell style={{ textAlign: 'center', minWidth: 80 }}></TableCell>
                            <TableCell style={{ textAlign: 'center', minWidth: 20 }}></TableCell>
                            <TableCell style={{ textAlign: 'center', minWidth: 20 }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredDatas &&
                            filteredDatas.map((emailTemplate: any) => (
                                <TableRow key={emailTemplate.id}>
                                    <TableCell style={{ textAlign: 'center' }}>{emailTemplate.templateName}</TableCell>
                                    <TableCell style={{ textAlign: 'center' }}>{emailTemplate.teamName}</TableCell>
                                    <TableCell style={{ textAlign: 'center' }}>{emailTemplate.createdBy}</TableCell>
                                    <TableCell style={{ textAlign: 'center' }}>
                                        {emailTemplate.createdDateStr}
                                    </TableCell>
                                    <TableCell style={{ textAlign: 'center' }}>
                                        <Tooltip title={t('jsonTemplateBuilder.showPreview')}>
                                            <Button
                                                className={DJBL.inputButtonPreview}
                                                variant="text"
                                                style={{ color: '#0d99ff', fontWeight: 'bold' }}
                                                startIcon={<VisibilityIcon />}
                                            >
                                                {t('jsonTemplateBuilder.showPreview')}
                                            </Button>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell style={{ textAlign: 'center' }}>
                                        <Tooltip title={t('jsonTemplateBuilder.modifyStructure')}>
                                            <Button
                                                className={DJBL.inputButtonModifyStructure}
                                                variant="text"
                                                style={{ color: '#0d99ff', fontWeight: 'bold' }}
                                                startIcon={<AccountTreeIcon />}
                                                onClick={() => history.push(`#`)}
                                            >
                                                {t('jsonTemplateBuilder.modifyStructure')}
                                            </Button>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <PopupState variant="popover" popupId="demo-popup-menu">
                                            {(popupState: any) => (
                                                <React.Fragment>
                                                    <IconButton className={DJBL.inputIconRowMenu} {...bindTrigger(popupState)}>
                                                        {' '}
                                                        <MoreVertIcon className="svg_icons" />{' '}
                                                    </IconButton>
                                                    <Menu {...bindMenu(popupState)}>
                                                        <MenuItem
                                                            onClick={() =>
                                                                handleClick(
                                                                    popupState,
                                                                    'edit',
                                                                    emailTemplate.templateId
                                                                )
                                                            }
                                                        >
                                                            {t('general.edit')}{' '}
                                                        </MenuItem>
                                                        <MenuItem
                                                            onClick={() =>
                                                                handleClick(
                                                                    popupState,
                                                                    'delete',
                                                                    emailTemplate.templateId
                                                                )
                                                            }
                                                        >
                                                            {t('general.delete')}{' '}
                                                        </MenuItem>
                                                    </Menu>
                                                </React.Fragment>
                                            )}
                                        </PopupState>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
                <Grid className={DJBL.paginationWrap}>
                    <Stack spacing={2} style={{ alignItems: 'end' }}>
                        <Pagination
                            count={allRowCount}
                            color="primary"
                            page={currentPage}
                            onChange={handlePageChange}
                        />
                    </Stack>
                </Grid>
            </TableContainer>
        </>
    );
};
